const BULLET_LINE_WITH_TEXT_PATTERN = /^[ \t]*[•●▪◦][ \t]+(.+\S.*)$/gm;
const BULLET_ONLY_LINE_PATTERN = /^[ \t]*[•●▪◦][ \t]*\n[ \t]*(?:\n[ \t]*)*/gm;
const DASH_BULLET_WITH_WRAPPED_TEXT_PATTERN =
  /^[ \t]*-[ \t]*\n[ \t]*(\S.*)$/gm;
const INDENTED_DASH_LIST_ITEM_PATTERN = /^[ \t]{4,}-[ \t]+(.+\S.*)$/gm;
const FENCED_BLOCK_PATTERN = /```([a-zA-Z0-9_-]*)\s*\n([\s\S]*?)\n?```/g;
const LIST_MARKER_PATTERN = /^(\s*)([-*+]|\d+\.)\s+(.*)$/;

const unwrapFencedMarkdownBlocks = (value: string): string => {
  return value.replace(
    FENCED_BLOCK_PATTERN,
    (match: string, rawLanguage: string, body: string) => {
      const language = rawLanguage.trim().toLowerCase();
      if (!language || language === "markdown" || language === "md") {
        return body;
      }

      return match;
    }
  );
};

const compactMultilineListItems = (value: string): string => {
  const lines = value.split("\n");
  const compactedLines: string[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    const markerMatch = line.match(LIST_MARKER_PATTERN);

    if (!markerMatch) {
      compactedLines.push(line);
      index += 1;
      continue;
    }

    const markerIndent = markerMatch[1].length;
    let compactedLine = `${markerMatch[1]}${markerMatch[2]} ${markerMatch[3].trim()}`;
    index += 1;

    while (index < lines.length) {
      const nextLine = lines[index];
      const nextMarkerMatch = nextLine.match(LIST_MARKER_PATTERN);

      if (nextMarkerMatch && nextMarkerMatch[1].length <= markerIndent) {
        break;
      }

      if (!nextLine.trim()) {
        index += 1;
        continue;
      }

      if (nextMarkerMatch && nextMarkerMatch[1].length > markerIndent) {
        break;
      }

      const nextIndent = (nextLine.match(/^\s*/) || [""])[0].length;
      if (nextIndent > markerIndent) {
        compactedLine = `${compactedLine} ${nextLine.trim()}`;
        index += 1;
        continue;
      }

      break;
    }

    compactedLines.push(compactedLine);
  }

  return compactedLines.join("\n");
};

const normalizeEscapedNewlines = (value: string): string => {
  // Some backend responses arrive with literal "\n" sequences instead of
  // actual newline characters; decode them so markdown can render lists.
  return value
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n");
};

export const normalizeMarkdownText = (value: string): string => {
  if (!value) {
    return "";
  }

  const normalized = normalizeEscapedNewlines(value).replace(/\r\n?/g, "\n");

  const normalizedListText = unwrapFencedMarkdownBlocks(normalized)
    .replace(INDENTED_DASH_LIST_ITEM_PATTERN, "- $1")
    .replace(DASH_BULLET_WITH_WRAPPED_TEXT_PATTERN, "- $1")
    .replace(BULLET_LINE_WITH_TEXT_PATTERN, "- $1")
    .replace(BULLET_ONLY_LINE_PATTERN, "- ");

  return compactMultilineListItems(normalizedListText);
};
